import { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useForm } from 'react-hook-form'

import { isPlatformSuperAdmin } from 'pure-js/libs/isSuperUser'
import { OrgRole } from 'pure-js/types/OrganizationMembership'

import * as organizationProcessHandlers from '../../../../apiLayer/processHandlers/organizations'
import { State } from '../../../../hooks/useAppState'
import { getActiveOrganizationId } from '../../../organization/orgStateHelpers'
import { InviteUsersFormValues, UserRowValues } from './InviteUserRow'
import { buildResolver, createValidationSchema, defaultFormValues, TranslateFn } from './inviteUsersFormConfig'
import type { InviteUsersSendResult } from './inviteUsers.types'

export type InviteUsersWorkflowParams = {
  isOpen: boolean
  onClose: () => void
  t: TranslateFn
  state: State
}

export const useInviteUsersWorkflow = ({ isOpen, onClose, t, state }: InviteUsersWorkflowParams) => {
  const validationSchema = useMemo(() => createValidationSchema(t), [t])
  const resolver = useMemo(() => buildResolver<InviteUsersFormValues>(validationSchema), [validationSchema])

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting }
  } = useForm<InviteUsersFormValues>({
    resolver,
    defaultValues: defaultFormValues
  })

  const [users, setUsers] = useState<UserRowValues[]>([])
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [isConfirming, setIsConfirming] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [serverError, setServerError] = useState('')
  const [result, setResult] = useState<InviteUsersSendResult | null>(null)

  const previewUsers = useMemo(
    () =>
      users.map((user, index) => ({
        id: `${index}-${user.email}`,
        name: user.name,
        email: user.email
      })),
    [users]
  )

  const totalUsers = users.length
  const actionDisabled = isSending
  const hasPendingUser = users.length > 0
  const isClearDisabled = actionDisabled || users.length === 0

  useEffect(() => {
    if (isOpen) {
      reset(defaultFormValues)
      setUsers([])
      setEditingIndex(null)
      setIsConfirming(false)
      setResult(null)
      setServerError('')
    }
  }, [isOpen, reset])

  const handleClose = useCallback(() => {
    if (isSubmitting || isSending) return
    onClose()
  }, [isSubmitting, isSending, onClose])

  const handleClearList = useCallback(() => {
    reset(defaultFormValues)
    setUsers([])
    setEditingIndex(null)
    setIsConfirming(false)
    setResult(null)
    setServerError('')
  }, [reset])

  const handleRestart = useCallback(() => {
    handleClearList()
  }, [handleClearList])

  const handleSaveUser = handleSubmit((values) => {
    const normalized: UserRowValues = {
      name: values.name.trim(),
      email: values.email.trim().toLowerCase()
    }

    const duplicateIndex = users.findIndex((user, index) => user.email === normalized.email && index !== editingIndex)

    if (duplicateIndex !== -1) {
      setError('email', {
        type: 'manual',
        message: t('facilitator.profile.inviteUsers.validation.duplicates')
      })
      return
    }

    setUsers((prev) => {
      if (editingIndex === null) {
        return [...prev, normalized]
      }
      return prev.map((user, index) => (index === editingIndex ? normalized : user))
    })

    reset(defaultFormValues)
    setEditingIndex(null)
    setIsConfirming(false)
    setServerError('')
  })

  const handleSelectUser = useCallback(
    (index: number) => {
      const user = users[index]
      if (!user) return
      reset({ name: user.name, email: user.email })
      setEditingIndex(index)
      setIsConfirming(false)
      setServerError('')
    },
    [reset, users]
  )

  const handleRemoveFromList = useCallback(
    (index: number) => {
      setUsers((prev) => prev.filter((_, idx) => idx !== index))
      if (editingIndex === index) {
        reset(defaultFormValues)
        setEditingIndex(null)
      } else if (editingIndex !== null && index < editingIndex) {
        setEditingIndex((prev) => (prev !== null ? prev - 1 : null))
      }
      setIsConfirming(false)
      setServerError('')
    },
    [editingIndex, reset]
  )

  const handlePrepareConfirmation = useCallback(() => {
    if (users.length === 0) {
      setServerError(t('facilitator.profile.inviteUsers.validation.minUsers'))
      return
    }
    setServerError('')
    setIsConfirming(true)
  }, [t, users.length])

  const handleCancelConfirmation = useCallback(() => {
    setIsConfirming(false)
  }, [])

  const handleConfirmSend = useCallback(async () => {
    if (users.length === 0) {
      setServerError(t('facilitator.profile.inviteUsers.validation.minUsers'))
      return
    }

    setServerError('')
    setIsSending(true)

    try {
      const organizationId = getActiveOrganizationId(state.user)
      if (!organizationId) {
        throw new Error(t('facilitator.profile.inviteUsers.errorGeneric'))
      }
      const membership = state.user?.organizations?.find((m) => m.organizationId === organizationId)
      const canInvite = isPlatformSuperAdmin(state.user) || membership?.role === OrgRole.ADMIN

      if (!canInvite) {
        throw new Error(t('facilitator.profile.inviteUsers.notOrgAdmin'))
      }

      const response = await organizationProcessHandlers.inviteUsersToOrganization(state, {
        organizationId,
        role: OrgRole.GAMER,
        users: users.map((row) => ({ email: row.email.trim().toLowerCase(), name: row.name.trim() }))
      })

      const invitedUsers: InviteUsersSendResult['invitedUsers'] = []
      const failedUsers: NonNullable<InviteUsersSendResult['failedUsers']> = []

      if (response.success && response.data) {
        for (const row of response.data.invitedUsers) {
          invitedUsers.push({ email: row.email })
        }
        for (const row of response.data.failedUsers) {
          failedUsers.push({ email: row.email, error: row.error })
        }
      } else {
        throw new Error(response.error || t('facilitator.profile.inviteUsers.errorGeneric'))
      }

      if (invitedUsers.length === 0) {
        const firstErr = failedUsers[0]?.error
        throw new Error(
          firstErr?.includes('org-capacity') || firstErr?.includes('resource-exhausted')
            ? t('facilitator.profile.inviteUsers.orgCapacityExceeded')
            : firstErr || t('facilitator.profile.inviteUsers.errorGeneric')
        )
      }

      setResult({
        success: true,
        invitedUsers,
        failedUsers: failedUsers.length > 0 ? failedUsers : undefined
      })
      toast.success(
        t('facilitator.profile.inviteUsers.successToast', {
          count: invitedUsers.length
        })
      )
      reset(defaultFormValues)
      setUsers([])
      setEditingIndex(null)
      setIsConfirming(false)
    } catch (error) {
      const message = error instanceof Error ? error.message : t('facilitator.profile.inviteUsers.errorGeneric')
      setServerError(message)
      toast.error(message)
      setIsConfirming(false)
    } finally {
      setIsSending(false)
    }
  }, [reset, state, t, users])

  return {
    control,
    errors,
    isSubmitting,
    editingIndex,
    previewUsers,
    totalUsers,
    isConfirming,
    serverError,
    actionDisabled,
    hasPendingUser,
    isClearDisabled,
    result,
    handleClose,
    handleSaveUser,
    handleClearList,
    handlePrepareConfirmation,
    handleCancelConfirmation,
    handleConfirmSend,
    handleSelectUser,
    handleRemoveFromList,
    handleRestart
  }
}
